import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../lib/apiClient";
import LocationAccordionFilter from "../../../shared/LocationAccordionFilter";
import {
  DogIconOutlined,
  CatIconOutlined,
  DogIconFilled,
  CatIconFilled,
} from "../../../shared/Icons/CustomIcons";
import Tooltip from "../../../shared/Tooltip";
import { MapPin } from "lucide-react";
import { KennelMapLinkButton } from "../../../features/kennelMap/KennelMapLinkButton.js";

type CityAvailability = { city: string; hasDogs: boolean; hasCats: boolean };

interface PetTypeButtonProps {
  pet: "cao" | "gato";
  label: string;
  selected: boolean;
  hovered: boolean;
  disabled: boolean;
  layout?: "row" | "column";
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function PetTypeButton({
  pet,
  label,
  selected,
  hovered,
  disabled,
  layout = "row",
  onClick,
  onMouseEnter,
  onMouseLeave,
}: PetTypeButtonProps) {
  const isDog = pet === "cao";
  const FilledIcon = isDog ? DogIconFilled : CatIconFilled;
  const OutlinedIcon = isDog ? DogIconOutlined : CatIconOutlined;
  const colorClassName = selected
    ? isDog
      ? "border-main-purple bg-main-purple text-white shadow-lg"
      : "border-main-blue bg-main-blue text-white shadow-lg"
    : isDog
      ? "bg-light-purple border border-main-purple text-main-purple hover:bg-main-purple hover:text-white hover:border-main-purple active:bg-main-purple active:text-white"
      : "bg-light-blue border border-main-blue text-main-blue hover:bg-main-blue hover:text-white hover:border-main-blue active:bg-main-blue active:text-white";
  const iconSize = selected || hovered
    ? isDog
      ? 27
      : layout === "row"
        ? 26
        : 25
    : isDog
      ? layout === "row"
        ? 27
        : 26
      : 27;
  const labelClassName = layout === "row" ? "font-medium text-sm group-hover:text-white active:text-white" : "font-medium text-md group-hover:text-white active:text-white";

  return (
    <div
      className={layout === "row" ? "relative flex-1" : "relative"}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        className={`${layout === "row" ? "w-full flex items-center justify-center gap-2 px-4 py-3" : "flex min-w-[80px] flex-col items-center justify-center p-3 text-center"} group relative overflow-hidden rounded-lg transition-all duration-300 shadow-button-box hover:shadow-md ${colorClassName} ${disabled ? "cursor-not-allowed opacity-50" : ""} ${selected ? "animate-shake" : ""}`.trim()}
      >
        {selected || hovered ? (
          <FilledIcon size={iconSize} />
        ) : (
          <OutlinedIcon size={iconSize} />
        )}
        <span className={labelClassName}>{label}</span>
      </button>
    </div>
  );
}

/**
 * Static, dropdown-free stand-in for the location picker, used inside the
 * onboarding carousel. Clicking it simply fills in "A tua localização" so no
 * expanding list can overflow the slide.
 */
function SimpleLocationField({
  value,
  onFill,
}: {
  value: string[];
  onFill: () => void;
}) {
  const { t } = useTranslation("landing");
  const filled = value.length > 0;
  return (
    <button
      type="button"
      onClick={onFill}
      aria-label={t("heroForm.setYourLocation")}
      className="w-full min-w-[200px] flex items-center gap-2 px-4 py-3 rounded-lg border border-main-purple bg-white text-left transition-colors duration-200 hover:bg-light-purple focus:outline-none focus:ring-2 focus:ring-main-purple"
    >
      <MapPin className="w-5 h-5 text-main-purple flex-shrink-0" />
      <span
        className={`font-medium ${filled ? "text-my-black" : "text-gray-400"}`}
      >
        {filled ? value.join(", ") : t("heroForm.allPortugal")}
      </span>
    </button>
  );
}

// Hero Search Form Component
type HeroSearchFormProps = {
  selectedPet?: "cao" | "gato" | null;
  setSelectedPet?: (pet: "cao" | "gato" | null) => void;
  /**
   * When provided, the form does NOT navigate on submit; this callback runs
   * instead (used to embed the form inside onboarding as a live demo).
   */
  onSubmitOverride?: (selection: {
    petType: "cao" | "gato";
    cities: string[];
  }) => void;
  /**
   * When true, replaces the city dropdown with a static field that just fills
   * in "A tua localização" on click (used inside onboarding, where an expanding
   * dropdown would overflow the slide vertically).
   */
  simplifiedLocation?: boolean;
};

export const HeroSearchForm = ({
  selectedPet: selectedPetProp,
  setSelectedPet: setSelectedPetProp,
  onSubmitOverride,
  simplifiedLocation = false,
}: HeroSearchFormProps) => {
  const { t } = useTranslation(["landing", "common"]);
  const [selectedPet, setSelectedPet] = useState<"cao" | "gato" | null>(null);
  const [location, setLocation] = useState<string[]>([]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);
  const [hoveredPet, setHoveredPet] = useState<"cao" | "gato" | null>(null);
  const navigate = useNavigate();

  // Fetch only cities from API (breeds and colors not needed on landing page)
  const [cities, setCities] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [citiesByDistrict, setCitiesByDistrict] = useState<
    Record<string, string[]>
  >({});
  const [cityAvailability, setCityAvailability] = useState<CityAvailability[]>(
    [],
  );
  const [metadataLoading, setMetadataLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        // Fetch cities for all animal types (dogs and cats combined)
        const data = await apiClient.get<{
          success: boolean;
          cities: string[];
          districts?: string[];
          citiesByDistrict?: Record<string, string[]>;
          cityAvailability?: CityAvailability[];
        }>("/api/kennels/cities?type=all");
        if (data.success && data.cities) {
          setCities(data.cities);
          setDistricts(data.districts ?? []);
          setCitiesByDistrict(data.citiesByDistrict ?? {});
          // Backward compatible: older backends omit cityAvailability.
          setCityAvailability(data.cityAvailability ?? []);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchCities();
  }, []);

  // Detect user location using browser geolocation API
  const detectLocation = async () => {
    setIsDetectingLocation(true);
    try {
      // Try browser geolocation first
      if ("geolocation" in navigator) {
        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 10000,
              enableHighAccuracy: false,
            });
          },
        );

        // Reverse geocode to get city name
        const { latitude, longitude } = position.coords;
        const response = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=pt`,
        );

        if (response.ok) {
          const data = await response.json();
          const detectedCity = data.city || data.locality || "Porto";
          setLocation([detectedCity]);
          return;
        }
      }

      // Fallback to IP geolocation if browser geolocation fails
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) throw new Error("Failed to detect location");
      const data = await response.json();
      const detectedCity = data.city || "Porto";
      setLocation([detectedCity]);
    } catch (error) {
      console.error("Location detection failed:", error);
      // Default to Porto if everything fails
      setLocation(["Porto"]);
    } finally {
      setIsDetectingLocation(false);
    }
  };

  // Support controlled prop overrides from parent
  const selected =
    selectedPetProp !== undefined ? selectedPetProp : selectedPet;
  const setSelected = setSelectedPetProp ?? setSelectedPet;

  // Map of city -> which species are available, for dynamic disabling.
  const availabilityByCity = new Map<
    string,
    { hasDogs: boolean; hasCats: boolean }
  >();
  if (cityAvailability.length > 0) {
    for (const entry of cityAvailability) {
      availabilityByCity.set(entry.city, {
        hasDogs: entry.hasDogs,
        hasCats: entry.hasCats,
      });
    }
  } else {
    // Backward compatible: no availability data -> assume both available.
    for (const city of cities) {
      availabilityByCity.set(city, { hasDogs: true, hasCats: true });
    }
  }

  // A pet type is available if no city is chosen, or at least one selected
  // city has that species. Skipped in the simplified onboarding demo.
  const dogAvailable =
    simplifiedLocation ||
    location.length === 0 ||
    location.some((c) => availabilityByCity.get(c)?.hasDogs ?? true);
  const catAvailable =
    simplifiedLocation ||
    location.length === 0 ||
    location.some((c) => availabilityByCity.get(c)?.hasCats ?? true);

  const dogUnavailableMsg = t("heroForm.noDogsIn", { cities: location.join(", ") });
  const catUnavailableMsg = t("heroForm.noCatsIn", { cities: location.join(", ") });

  const handlePetSelection = (petType: "cao" | "gato") => {
    if (petType === "cao" && !dogAvailable) return;
    if (petType === "gato" && !catAvailable) return;
    setSelected(petType);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (selected) {
      if (onSubmitOverride) {
        onSubmitOverride({ petType: selected, cities: location });
        return;
      }
      // Navigate to the respective page
      // Join locations for navigation state (to be compatible with existing code)
      const locationString = location.length > 0 ? location.join(",") : "";
      navigate(selected === "cao" ? "/caes" : "/gatos", {
        state: {
          petType: selected,
          location: locationString,
          cities: location, // Pass cities array for filters
        },
      });
    }
  };

  return (
    <form
      onSubmit={handleSearch}
      className="bg-floating-bg backdrop-blur-sm rounded-3xl pb-4 sm:p-8 shadow-2xl max-w-4xl mx-auto border relative z-[100] mb-8"
    >
      {/* Mobile Layout - Stacked */}
      <div className="flex flex-col sm:hidden gap-4 p-4">
        {/* Pet Type Buttons - Mobile */}
        <div className="flex gap-2 justify-center">
          <PetTypeButton
            pet="cao"
            label={t("common:animal.dogs")}
            selected={selected === "cao"}
            hovered={hoveredPet === "cao"}
            disabled={!dogAvailable}
            layout="row"
            onClick={() => handlePetSelection("cao")}
            onMouseEnter={() => setHoveredPet("cao")}
            onMouseLeave={() => setHoveredPet(null)}
          />
          {!dogAvailable && hoveredPet === "cao" && (
            <Tooltip>{dogUnavailableMsg}</Tooltip>
          )}

          <PetTypeButton
            pet="gato"
            label={t("common:animal.cats")}
            selected={selected === "gato"}
            hovered={hoveredPet === "gato"}
            disabled={!catAvailable}
            layout="row"
            onClick={() => handlePetSelection("gato")}
            onMouseEnter={() => setHoveredPet("gato")}
            onMouseLeave={() => setHoveredPet(null)}
          />
          {!catAvailable && hoveredPet === "gato" && (
            <Tooltip>{catUnavailableMsg}</Tooltip>
          )}
        </div>

        {/* Location Select - Mobile */}
        <div className="relative flex items-end gap-2">
          <div className="min-w-0 flex-1">
          {simplifiedLocation ? (
            <SimpleLocationField
              value={location}
              onFill={() => setLocation([t("heroForm.yourLocation")])}
            />
          ) : (
            <LocationAccordionFilter
              label={t("heroForm.location")}
              value={location}
              onChange={setLocation}
              placeholder={t("heroForm.allPortugal")}
              districts={districts}
              citiesByDistrict={citiesByDistrict}
              clearAllText={t("heroForm.clearAllCities")}
              disabled={isDetectingLocation}
            />
          )}
          </div>
          {!simplifiedLocation && (
            <KennelMapLinkButton
              variant="labeled"
              species={selected ?? undefined}
              label={t("heroForm.viewOnMap")}
              className="h-12"
            />
          )}
          {/* Detect location button */}
          {/* <button
            type="button"
            onClick={detectLocation}
            disabled={isDetectingLocation}
            className="mt-2 w-full px-4 py-3 text-sm font-medium text-button-text border-2  rounded-xl hover:bg-lighter-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
          >
            📍 {isDetectingLocation ? "A procurar..." : "Procurar onde estou"}
          </button> */}
        </div>

        {/* Search Button - Mobile */}
        <div
          className="relative"
          onMouseEnter={() => {
            if (!selected) setShowDisabledTooltip(true);
          }}
          onMouseLeave={() => setShowDisabledTooltip(false)}
        >
          <button
            type="submit"
            disabled={!selected}
            className={`w-full py-3 rounded-xl font-semibold  transition-all duration-300
              ${
                selected
                  ? "text-white bg-hero-gradient  hover:bg-main-purple hover:scale-105 hover:shadow-lg animate-shake"
                  : "shadow bg-inactive-button border border-inactive-grey-border text-gray-500 cursor-not-allowed"
              }
            `}
          >
            {t("heroForm.search")}
          </button>

          {!selected && showDisabledTooltip && (
            <Tooltip>{t("heroForm.chooseOne")}</Tooltip>
          )}
        </div>
      </div>

      {/* Desktop Layout - Horizontal */}
      <div className="hidden sm:flex gap-4 items-end">
        {/* Pet Type Buttons - Desktop */}
        <div className="flex flex-col items-start">
          <div className="flex gap-2">
            <PetTypeButton
              pet="cao"
              label={t("common:animal.dogs")}
              selected={selected === "cao"}
              hovered={hoveredPet === "cao"}
              disabled={!dogAvailable}
              layout="column"
              onClick={() => handlePetSelection("cao")}
              onMouseEnter={() => setHoveredPet("cao")}
              onMouseLeave={() => setHoveredPet(null)}
            />
            {!dogAvailable && hoveredPet === "cao" && (
              <Tooltip>{dogUnavailableMsg}</Tooltip>
            )}

            <PetTypeButton
              pet="gato"
              label={t("common:animal.cats")}
              selected={selected === "gato"}
              hovered={hoveredPet === "gato"}
              disabled={!catAvailable}
              layout="column"
              onClick={() => handlePetSelection("gato")}
              onMouseEnter={() => setHoveredPet("gato")}
              onMouseLeave={() => setHoveredPet(null)}
            />
            {!catAvailable && hoveredPet === "gato" && (
              <Tooltip>{catUnavailableMsg}</Tooltip>
            )}
          </div>
        </div>

        {/* Location Select - Desktop */}
        <div className="relative flex flex-1 items-end gap-2">
          <div className="min-w-0 flex-1">
            {simplifiedLocation ? (
              <SimpleLocationField
                value={location}
                onFill={() => setLocation([t("heroForm.yourLocation")])}
              />
            ) : (
              <LocationAccordionFilter
                label={t("heroForm.location")}
                value={location}
                onChange={setLocation}
                placeholder={t("heroForm.allPortugal")}
                districts={districts}
                citiesByDistrict={citiesByDistrict}
                clearAllText={t("heroForm.clearAllCities")}
                disabled={isDetectingLocation}
              />
            )}
          </div>
          {!simplifiedLocation && (
            <KennelMapLinkButton
              variant="labeled"
              species={selected ?? undefined}
              label={t("heroForm.viewOnMap")}
              className="h-12"
            />
          )}
          {/* Detect location button */}
          {/* <button
            type="button"
            onClick={detectLocation}
            disabled={isDetectingLocation}
            className="w-full px-4 py-2 text-sm font-medium text-button-text border-2  rounded-lg hover:bg-lighter-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-button-box"
          >
            📍 {isDetectingLocation ? "A procurar..." : "Procurar onde estou"}
          </button> */}
        </div>

        {/* Search Button - Desktop */}
        <div
          className="relative"
          onMouseEnter={() => {
            if (!selected) setShowDisabledTooltip(true);
          }}
          onMouseLeave={() => setShowDisabledTooltip(false)}
        >
          <button
            type="submit"
            disabled={!selected}
            className={`px-6 py-3 rounded-lg font-semibold  transition-all duration-300 text-button-text whitespace-nowrap shadow-button-box
              ${
                selected
                  ? "bg-hero-gradient text-white hover:bg-main-purple hover:scale-105 hover:shadow-lg animate-shake"
                  : `bg-inactive-button border border-inactive-grey-border text-gray-500 cursor-not-allowed`
              }
            `}
          >
            {t("heroForm.search")}
          </button>
          {!selected && showDisabledTooltip && (
            <Tooltip>{t("heroForm.chooseOne")}</Tooltip>
          )}{" "}
        </div>
      </div>
    </form>
  );
};

export default HeroSearchForm;
